import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Search, MapPin, ArrowRight, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", type: "client", sector: "", city: "", vat: "", email: "", phone: "", website: "", address: "", status: "active", notes: "" };

function CompanyDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.contacts_count; delete payload.projects_count;
      if (editing) await api.patch(`/companies/${initial.id}`, payload);
      else await api.post("/companies", payload);
      toast.success(editing ? "Empresa actualizada" : "Empresa creada");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle><DialogDescription className="sr-only">Formulario de empresa</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5">
            <Label>Nombre *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="company-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="company-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Cliente</SelectItem>
                <SelectItem value="prospect">Prospecto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Sector</Label><Input value={form.sector} onChange={(e) => set("sector", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Ciudad</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>NIF/CIF</Label><Input value={form.vat} onChange={(e) => set("vat", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Web</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Dirección</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="resize-none" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="company-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Empresas() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [companies, setCompanies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Vista "solo búsqueda": no se cargan todos los registros al entrar.
  const term = search.trim();
  const active = term.length >= 2 || type !== "all";

  const load = useCallback(async () => {
    if (!active) { setCompanies(null); return; }
    setLoading(true);
    try {
      const params = { ...(term ? { search: term } : {}), ...(type !== "all" ? { type } : {}) };
      const r = await api.get("/companies", { params });
      setCompanies(r.data);
    } catch { setCompanies([]); }
    finally { setLoading(false); }
  }, [term, type, active]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta empresa y sus contactos?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Empresa eliminada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Empresas" subtitle="Busca clientes y prospectos de la agencia">
        {can("crm", "create") && (
          <Button size="sm" className="gap-1.5" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-company-btn">
            <Plus className="h-4 w-4" /> Nueva empresa
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar empresa por nombre…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="company-search" autoFocus />
        </div>
        <Tabs value={type} onValueChange={setType}>
          <TabsList data-testid="company-filter">
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="client">Clientes</TabsTrigger>
            <TabsTrigger value="prospect">Prospectos</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {!active ? (
        <Card className="border-dashed border-border shadow-none py-20 flex flex-col items-center justify-center text-center gap-3" data-testid="company-search-empty">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Search className="h-6 w-6" /></div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Busca una empresa</p>
            <p className="text-xs text-muted-foreground max-w-xs">Escribe al menos 2 letras del nombre (o elige un filtro) para ver resultados. No se cargan todas las empresas de golpe.</p>
          </div>
        </Card>
      ) : (
        <Card className="border-border shadow-none overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
          ) : (companies || []).length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground" data-testid="company-no-results">No se encontraron empresas para «{term || (type === "client" ? "Clientes" : "Prospectos")}».</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Empresa</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Ubicación</TableHead>
                  <TableHead className="text-center">Contactos</TableHead>
                  <TableHead className="text-center">Proyectos</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((c) => (
                  <TableRow key={c.id} className="group" data-testid={`company-row-${c.id}`}>
                    <TableCell className="pl-5 cursor-pointer" onClick={() => navigate(`/crm/empresa/${c.id}`)}>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-sm">{c.name.slice(0, 2).toUpperCase()}</div>
                        <div className="font-medium text-sm group-hover:text-primary transition-colors duration-200">{c.name}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.sector || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.city ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.city}</span> : "—"}</TableCell>
                    <TableCell className="text-center text-sm tabular-nums">{c.contacts_count}</TableCell>
                    <TableCell className="text-center text-sm tabular-nums">{c.projects_count}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("border-transparent font-medium", c.type === "client" ? "bg-primary/12 text-primary" : "bg-gold/15 text-[#a9741c] dark:text-gold")}>
                        {c.type === "client" ? "Cliente" : "Prospecto"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {(can("crm", "edit") || can("crm", "delete")) ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`company-menu-${c.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {can("crm", "edit") && <DropdownMenuItem onClick={() => { setEditing(c); setDialogOpen(true); }}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>}
                            {can("crm", "delete") && <DropdownMenuItem onClick={() => del(c.id)} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Eliminar</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : <ArrowRight className="h-4 w-4 text-muted-foreground/40" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {dialogOpen && <CompanyDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />}
    </div>
  );
}
